import { useState, useMemo } from 'react';

import { RaceData } from '@/pages/race-wizard';
import { Calendar, Clock, Flag } from 'lucide-react';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const MIN_RACE_DURATION = 1;
const MAX_RACE_DURATION = 24;

const RaceSettingsView = ({
  onNext,
  onBack,
  currentStep,
  isLastStep,
  initialRaceSettings = {
    raceName: '',
    raceDuration: 1,
    startDate: '',
    startTime: '',
    startDateTime: '' as unknown as dayjs.Dayjs,
  },
}: {
  onNext: (settings: RaceData) => void;
  onBack: () => void;
  currentStep: number;
  isLastStep: boolean;
  initialRaceSettings?: RaceData;
}) => {
  const [raceName, setRaceName] = useState(initialRaceSettings.raceName || '');
  const [raceDuration, setRaceDuration] = useState(initialRaceSettings.raceDuration || 1);
  const [startDate, setStartDate] = useState(initialRaceSettings.startDate || '');
  const [startTime, setStartTime] = useState(initialRaceSettings.startTime || '');

  const handleRaceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRaceName(e.target.value);
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setRaceDuration(value);

    // Calculate percentage for the gradient
    const percentage = ((value - Number(e.target.min)) / (Number(e.target.max) - Number(e.target.min))) * 100;
    e.target.style.setProperty('--value', `${percentage}%`);

    // Update bubble position
    const bubble = e.target.parentElement?.querySelector('.value-bubble') as HTMLElement;
    if (bubble) {
      bubble.style.left = `${percentage}%`;
      bubble.textContent = value.toString();
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    if (
      dayjs(e.target.value, 'YYYY-MM-DD', true).isValid() &&
      (dayjs(e.target.value, 'YYYY-MM-DD', true).isSame(dayjs().startOf('day')) ||
        dayjs(e.target.value, 'YYYY-MM-DD', true).isAfter(dayjs().startOf('day')))
    ) {
      document.getElementById('startTime')?.focus();
    }
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartTime(e.target.value);
  };

  const handleSubmit = () => {
    // Prepare race settings data
    const raceSettings = {
      raceName,
      raceDuration,
      startDate,
      startTime,
      startDateTime: dayjs(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm', true),
    };

    onNext(raceSettings);
  };

  const canProceed =
    raceName &&
    startDate &&
    startTime &&
    dayjs(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm', true).isValid() &&
    dayjs(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm', true).isAfter(dayjs().add(1, 'hour'));

  const summary = useMemo(() => {
    const parsedDate = dayjs(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm', true);
    if (!parsedDate.isValid() || !parsedDate.isAfter(dayjs().add(1, 'hour')))
      return (
        <>
          Race start should be at least <span className="text-[#9F53E8]">1 hour </span>&nbsp;from now.
        </>
      );

    return (
      <>
        The race will start on the{' '}
        <span className="text-[#9F53E8]">
          {parsedDate.format('DD MMMM YYYY')} at {parsedDate.format('hh:mm A')}
        </span>
        , will{' '}
        <span className="text-[#9F53E8]">
          last for {raceDuration} {raceDuration === 1 ? 'hour' : 'hours'}
        </span>{' '}
        and <span>one can stake until {parsedDate.subtract(1, 'minute').format('hh:mm A')}</span>
      </>
    );
  }, [raceDuration, startDate, startTime]);

  return (
    <>
      <label className="block space-y-2 text-sm">
        <div className="flex items-center gap-2 font-medium">
          <Flag size={14} />
          Race Name
        </div>

        <input
          type="text"
          value={raceName}
          placeholder="Race Name"
          onChange={handleRaceNameChange}
          className="w-full rounded-lg border border-[#282133] bg-[#090409] px-4 py-2 text-base outline-none"
        />
      </label>

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 font-medium">
          <Clock size={16} />
          Race Duration
        </div>

        <div className="ml-auto flex w-[calc(100%-24px)] items-end gap-2">
          <div className="grow pb-2">
            <div className="flex items-center justify-between before:content-['1hr'] after:content-['24hrs']" />

            <input
              type="range"
              min={MIN_RACE_DURATION}
              max={MAX_RACE_DURATION}
              value={raceDuration}
              className="w-full"
              onChange={handleDurationChange}
              style={{ '--value': `${((raceDuration - 1) / 23) * 100}%` } as React.CSSProperties}
            />
          </div>

          <div className="relative flex w-[76px] items-center overflow-hidden rounded-lg border border-[#282133]">
            <span className="absolute right-2">hours</span>
            <input
              type="number"
              inputMode="numeric"
              max={MAX_RACE_DURATION}
              min={MIN_RACE_DURATION}
              value={raceDuration}
              onChange={handleDurationChange}
              className="h-[34px] w-full bg-transparent pl-2 pr-8 text-base outline-none"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 font-medium">
          <Calendar size={16} />
          Race Start
        </div>
        <div className="ml-auto flex w-[calc(100%-24px)] gap-2">
          <input
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            className="rounded-lg border border-[#282133] bg-[#090409] px-2 py-1 text-base outline-none"
            min={new Date().toISOString().split('T')[0]}
          />
          <input
            id="startTime"
            type="time"
            value={startTime}
            onChange={handleStartTimeChange}
            className="w-fit rounded-lg border border-[#282133] bg-[#090409] px-2 py-1 text-base outline-none"
          />
        </div>
      </div>

      <div className="max-w-xl">{summary}</div>

      <div className="flex items-center gap-4">
        {currentStep > 1 && (
          <button onClick={onBack} className="min-w-[40%] rounded-lg border border-[#9937F7] p-2 font-bold lg:min-w-40">
            Back
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={!canProceed || isLastStep}
          className="min-w-[40%] rounded-lg bg-[#9937F7] p-2 font-bold lg:min-w-40"
        >
          {isLastStep ? 'Finish' : 'Continue'}
        </button>
      </div>
    </>
  );
};

export default RaceSettingsView;
